import React, { useState, useEffect, useMemo } from 'react';
import { searchUsers, addFriend, getActivePlayerDirectory } from '../../api/platformApi';
import './AddFriendModal.css';

/**
 * AddFriendModal - Modal for picking or searching a user to add as a friend.
 *
 * Two discovery paths are offered because username search alone is unreliable
 * for players: the names shown on the Standings screen are TEAM display names
 * ("Amazing (Rock Hard)" = team name + known_as alias), NOT usernames. Typing a
 * standings name into a username search can never match. The dropdown lists the
 * real usernames of every active player so the user never has to guess.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onFriendAdded - Callback when a friend request is sent
 * @param {string} props.initialQuery - Initial search query (optional)
 * @param {string} props.currentUserPublicId - Public id of the signed-in user, so
 *        the directory can exclude self the same way the search endpoint does.
 */
function AddFriendModal({
    isOpen,
    onClose,
    onFriendAdded,
    initialQuery = '',
    currentUserPublicId = null,
}) {
    const [searchQuery, setSearchQuery] = useState(initialQuery);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [addingFriendId, setAddingFriendId] = useState(null);
    const [pendingFriendIds, setPendingFriendIds] = useState(new Set());
    const [addFriendMessages, setAddFriendMessages] = useState({});

    // Active-player dropdown state
    const [directoryPlayers, setDirectoryPlayers] = useState([]);
    const [isLoadingDirectory, setIsLoadingDirectory] = useState(false);
    const [directoryError, setDirectoryError] = useState(null);
    const [selectedPlayerId, setSelectedPlayerId] = useState('');

    // ERROR LOUDLY: Validate required props
    if (typeof isOpen !== 'boolean') {
        throw new Error('AddFriendModal: isOpen prop must be a boolean');
    }
    if (typeof onClose !== 'function') {
        throw new Error('AddFriendModal: onClose prop must be a function');
    }

    // Auto-search when modal opens with initial query
    useEffect(() => {
        if (isOpen && initialQuery && initialQuery.trim().length >= 3) {
            handleSearch(initialQuery);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialQuery]);

    // Load the active-player directory whenever the modal opens.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        let isCancelled = false;

        const loadDirectory = async () => {
            setIsLoadingDirectory(true);
            setDirectoryError(null);

            try {
                const data = await getActivePlayerDirectory();
                if (isCancelled) {
                    return;
                }
                if (!Array.isArray(data.players)) {
                    throw new Error(
                        'AddFriendModal: getActivePlayerDirectory returned a response without a players array'
                    );
                }
                setDirectoryPlayers(data.players);
            } catch (error) {
                if (isCancelled) {
                    return;
                }
                console.error('AddFriendModal: Error loading active player directory:', error);
                setDirectoryError(
                    error.message || 'Failed to load the player list. Use the search box instead.'
                );
                setDirectoryPlayers([]);
            } finally {
                if (!isCancelled) {
                    setIsLoadingDirectory(false);
                }
            }
        };

        loadDirectory();

        return () => {
            isCancelled = true;
        };
    }, [isOpen]);

    // Reset per-session UI state when the modal closes so a reopen starts clean.
    useEffect(() => {
        if (isOpen) {
            return;
        }
        setSelectedPlayerId('');
        setSearchResults([]);
        setSearchError(null);
        setAddFriendMessages({});
        setPendingFriendIds(new Set());
    }, [isOpen]);

    // The leaderboard-backed directory has no "exclude me" flag, so filter the
    // signed-in user out here to match SearchUsersView's exclude(id=request.user.id).
    const selectablePlayers = useMemo(() => {
        if (!currentUserPublicId) {
            return directoryPlayers;
        }
        return directoryPlayers.filter(player => player.public_id !== currentUserPublicId);
    }, [directoryPlayers, currentUserPublicId]);

    const selectedPlayer = useMemo(
        () => selectablePlayers.find(player => player.public_id === selectedPlayerId) || null,
        [selectablePlayers, selectedPlayerId]
    );

    const handleSearch = async (query = searchQuery) => {
        const trimmedQuery = typeof query === 'string' ? query.trim() : '';

        if (trimmedQuery.length < 3) {
            setSearchError('Please enter at least 3 characters to search');
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        setSearchError(null);

        try {
            const data = await searchUsers(trimmedQuery);
            if (!Array.isArray(data.users)) {
                throw new Error('AddFriendModal: searchUsers returned a response without a users array');
            }
            setSearchResults(data.users);

            if (data.users.length === 0) {
                setSearchError(
                    `No user found with "${trimmedQuery}" in their username. ` +
                    'Standings show team names, not usernames — pick the player from the dropdown above instead.'
                );
            }
        } catch (error) {
            console.error('AddFriendModal: Error searching users:', error);
            setSearchError(error.message || 'Failed to search users. Please try again.');
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleAddFriend = async (publicId, username) => {
        if (!publicId) {
            throw new Error('AddFriendModal: Cannot add friend without public_id');
        }

        setAddingFriendId(publicId);
        setAddFriendMessages(previousMessages => {
            const nextMessages = { ...previousMessages };
            delete nextMessages[publicId];
            return nextMessages;
        });

        try {
            const response = await addFriend({ public_id: publicId });
            const responsePublicId = response?.request?.to_public_id;
            if (response?.message !== 'Friend request sent' || responsePublicId !== publicId) {
                throw new Error(
                    `AddFriendModal: Unexpected friend-request response for ${username} ` +
                    `(public_id=${publicId}). Expected message="Friend request sent" and ` +
                    `request.to_public_id=${publicId}, received ${JSON.stringify(response)}`
                );
            }
            console.log(`AddFriendModal: Friend request sent to ${username}`);

            // POST /friends/add/ creates a PENDING request, not an ACTIVE friendship.
            // Keep that distinction visible; the friend list updates only after acceptance.
            setPendingFriendIds(previousIds => new Set(previousIds).add(publicId));

            // Notify parent component
            if (onFriendAdded && typeof onFriendAdded === 'function') {
                onFriendAdded(publicId, username);
            }
        } catch (error) {
            console.error('AddFriendModal: Error adding friend:', error);
            const message = error.message || 'Failed to add friend. Please try again.';

            if (message === 'Friend request already sent to this user') {
                // Search does not expose outgoing PENDING requests. A duplicate-request
                // response is therefore the authoritative local-session signal for this row.
                setPendingFriendIds(previousIds => new Set(previousIds).add(publicId));
                setAddFriendMessages(previousMessages => ({
                    ...previousMessages,
                    [publicId]: {
                        type: 'info',
                        text: 'You already sent a friend request to this user.',
                    },
                }));
                return;
            }

            setAddFriendMessages(previousMessages => ({
                ...previousMessages,
                [publicId]: {
                    type: 'error',
                    text: message,
                },
            }));
        } finally {
            setAddingFriendId(null);
        }
    };

    const handleSearchInputChange = (e) => {
        setSearchQuery(e.target.value);
        setSearchError(null);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        handleSearch();
    };

    const handlePlayerSelectChange = (e) => {
        setSelectedPlayerId(e.target.value);
    };

    const handleAddSelectedPlayer = () => {
        if (!selectedPlayer) {
            throw new Error(
                `AddFriendModal: Add was triggered with no selected player (selectedPlayerId="${selectedPlayerId}")`
            );
        }
        handleAddFriend(selectedPlayer.public_id, selectedPlayer.username);
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) {
        return null;
    }

    const selectedPlayerMessage = selectedPlayer ? addFriendMessages[selectedPlayer.public_id] : null;
    const selectedPlayerIsPending = selectedPlayer
        ? pendingFriendIds.has(selectedPlayer.public_id)
        : false;

    return (
        <div className="add-friend-modal-overlay" onClick={handleOverlayClick}>
            <div className="add-friend-modal">
                {/* Header */}
                <div className="add-friend-modal-header">
                    <h2 className="add-friend-modal-title">Add Friend</h2>
                    <button className="add-friend-modal-close" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                {/* Active-player dropdown */}
                <div className="add-friend-picker">
                    <label className="add-friend-picker-label" htmlFor="add-friend-player-select">
                        Choose an active player
                    </label>

                    {isLoadingDirectory ? (
                        <p className="add-friend-picker-status">Loading players...</p>
                    ) : directoryError ? (
                        <p className="add-friend-picker-status add-friend-picker-status-error" role="alert">
                            {directoryError}
                        </p>
                    ) : (
                        <>
                            <div className="add-friend-picker-row">
                                <select
                                    id="add-friend-player-select"
                                    className="add-friend-picker-select"
                                    value={selectedPlayerId}
                                    onChange={handlePlayerSelectChange}
                                >
                                    <option value="">
                                        {selectablePlayers.length === 0
                                            ? 'No other active players found'
                                            : `Select from ${selectablePlayers.length} active players...`}
                                    </option>
                                    {selectablePlayers.map(player => (
                                        <option key={player.public_id} value={player.public_id}>
                                            {player.teams.length > 0
                                                ? `${player.username} — ${player.teams.join(', ')}`
                                                : player.username}
                                        </option>
                                    ))}
                                </select>

                                {selectedPlayerIsPending ? (
                                    <span className="add-friend-request-pending">Request Sent</span>
                                ) : (
                                    <button
                                        type="button"
                                        className="add-friend-add-button"
                                        onClick={handleAddSelectedPlayer}
                                        disabled={
                                            !selectedPlayer ||
                                            addingFriendId === selectedPlayerId
                                        }
                                    >
                                        {addingFriendId === selectedPlayerId ? 'Adding...' : 'Add Selected'}
                                    </button>
                                )}
                            </div>

                            <p className="add-friend-picker-hint">
                                Standings list team names, not usernames — use this list to add the right player.
                            </p>

                            {selectedPlayerMessage && (
                                <p
                                    className={`add-friend-row-message add-friend-row-message-${selectedPlayerMessage.type}`}
                                    role={selectedPlayerMessage.type === 'error' ? 'alert' : 'status'}
                                >
                                    {selectedPlayerMessage.text}
                                </p>
                            )}
                        </>
                    )}
                </div>

                <div className="add-friend-divider">
                    <span className="add-friend-divider-text">or search by username</span>
                </div>

                {/* Search Input */}
                <form className="add-friend-search-form" onSubmit={handleSearchSubmit}>
                    <div className="add-friend-search-wrapper">
                        <svg
                            className="add-friend-search-icon"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <circle cx="11" cy="11" r="8"></circle>
                            <path d="m21 21-4.35-4.35"></path>
                        </svg>
                        <input
                            type="text"
                            className="add-friend-search-input"
                            placeholder="Search by username (min 3 characters)..."
                            value={searchQuery}
                            onChange={handleSearchInputChange}
                            autoFocus
                        />
                    </div>
                    <button
                        type="submit"
                        className="add-friend-search-button"
                        disabled={isSearching || searchQuery.trim().length < 3}
                    >
                        {isSearching ? 'Searching...' : 'Search'}
                    </button>
                </form>

                {/* Error Message */}
                {searchError && (
                    <div className="add-friend-error">
                        {searchError}
                    </div>
                )}

                {/* Search Results */}
                <div className="add-friend-results">
                    {isSearching ? (
                        <div className="add-friend-loading">
                            <div className="loading-spinner"></div>
                            <p>Searching for users...</p>
                        </div>
                    ) : searchResults.length > 0 ? (
                        <>
                            <div className="add-friend-results-header">
                                {searchResults.length} user{searchResults.length !== 1 ? 's' : ''} found
                            </div>
                            <div className="add-friend-results-list">
                                {searchResults.map((user) => {
                                    const addFriendMessage = addFriendMessages[user.public_id];

                                    return (
                                    <div key={user.public_id} className="add-friend-result-item">
                                        <div className="add-friend-user-info">
                                            <div className="add-friend-avatar">
                                                <img src="/art/UserIcon.png" alt="User" />
                                            </div>
                                            <div className="add-friend-user-meta">
                                                <div className="add-friend-username">{user.username}</div>
                                            </div>
                                        </div>
                                        <div className="add-friend-actions">
                                            {user.is_friend ? (
                                                <span className="add-friend-already-friends">Already Friends</span>
                                            ) : pendingFriendIds.has(user.public_id) ? (
                                                <span className="add-friend-request-pending">Request Sent</span>
                                            ) : (
                                                <button
                                                    className="add-friend-add-button"
                                                    onClick={() => handleAddFriend(user.public_id, user.username)}
                                                    disabled={addingFriendId === user.public_id}
                                                >
                                                    {addingFriendId === user.public_id ? 'Adding...' : 'Add Friend'}
                                                </button>
                                            )}
                                        </div>
                                        {addFriendMessage && (
                                            <p
                                                className={`add-friend-row-message add-friend-row-message-${addFriendMessage.type}`}
                                                role={addFriendMessage.type === 'error' ? 'alert' : 'status'}
                                            >
                                                {addFriendMessage.text}
                                            </p>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : searchQuery && !isSearching ? (
                        <div className="add-friend-empty">
                            <p>No results found. Try a different search term.</p>
                        </div>
                    ) : (
                        <div className="add-friend-empty">
                            <p>Pick a player from the list above, or search by their username.</p>
                            <p className="add-friend-hint">
                                Usernames are not the same as the team names shown on Standings.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AddFriendModal;
